import { urlApiProd } from '../vistas/utiles';

const SESSION_KEYS = ['token', 'access_token', 'refresh_token', 'loggedIn', 'Nombre', 'Rol', 'RolMenu', 'ID', 'ID_Usuario', 'correo'];
let refreshPromise = null;

function decodeJwtPayload(token) {
    try {
        const parts = String(token || '').split('.');
        if (parts.length !== 3) return null;
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
        const json = atob(padded);
        return JSON.parse(json);
    } catch {
        return null;
    }
}

export function isTokenExpired(token) {
    const payload = decodeJwtPayload(token);
    if (!payload || !payload.exp) return true;
    const nowSec = Math.floor(Date.now() / 1000);
    return payload.exp <= nowSec;
}

export function hasValidSession() {
    const accessToken = localStorage.getItem('access_token') || localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refresh_token');
    if (!accessToken || !refreshToken) return false;
    return !isTokenExpired(refreshToken);
}

export function clearSessionStorage() {
    SESSION_KEYS.forEach((k) => localStorage.removeItem(k));
}

export function logoutAndRedirect(reason = 'invalid_session') {
    clearSessionStorage();
    const target = `/?reason=${encodeURIComponent(reason)}`;
    if (window.location.pathname !== '/') {
        window.location.href = target;
        return;
    }
    window.history.replaceState({}, '', target);
    window.dispatchEvent(new Event('session:ended'));
}

function isAuthEndpoint(url) {
    const str = String(url || '');
    return str.includes('/login') || str.includes('/refresh') || str.includes('/logout');
}

async function refreshAccessToken(nativeFetch) {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken || isTokenExpired(refreshToken)) {
            throw new Error('Refresh token expired');
        }

        const res = await nativeFetch(`${urlApiProd}/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken })
        });

        if (!res.ok) {
            throw new Error(`Refresh failed: ${res.status}`);
        }

        const data = await res.json();
        const newAccessToken = data.access_token || data.token;
        const newRefreshToken = data.refresh_token;

        if (!newAccessToken || !newRefreshToken) {
            throw new Error('Refresh response missing tokens');
        }

        localStorage.setItem('token', newAccessToken);
        localStorage.setItem('access_token', newAccessToken);
        localStorage.setItem('refresh_token', newRefreshToken);
        localStorage.setItem('loggedIn', 'true');
        return newAccessToken;
    })();

    try {
        return await refreshPromise;
    } finally {
        refreshPromise = null;
    }
}

export function installAuthFetchInterceptor() {
    if (window.__authFetchInstalled) return;
    window.__authFetchInstalled = true;

    const nativeFetch = window.fetch.bind(window);

    window.fetch = async (input, init = {}) => {
        const url = typeof input === 'string' ? input : input?.url;
        const isAuthCall = isAuthEndpoint(url);

        let accessToken = localStorage.getItem('access_token') || localStorage.getItem('token');
        const refreshToken = localStorage.getItem('refresh_token');

        if (!isAuthCall && accessToken && isTokenExpired(accessToken)) {
            try {
                accessToken = await refreshAccessToken(nativeFetch);
            } catch {
                logoutAndRedirect('expired');
                throw new Error('Session expired');
            }
        }

        const headers = new Headers(init.headers || {});
        if (!headers.has('Authorization') && accessToken && !isAuthCall) {
            headers.set('Authorization', `Bearer ${accessToken}`);
        }

        let response = await nativeFetch(input, { ...init, headers });

        if (response.status === 401 && !isAuthCall && refreshToken) {
            try {
                const newAccessToken = await refreshAccessToken(nativeFetch);
                const retryHeaders = new Headers(init.headers || {});
                retryHeaders.set('Authorization', `Bearer ${newAccessToken}`);
                response = await nativeFetch(input, { ...init, headers: retryHeaders });
            } catch {
                logoutAndRedirect('unauthorized');
                throw new Error('Unauthorized');
            }
        } else if (response.status === 401 && isAuthCall) {
            logoutAndRedirect('unauthorized');
            throw new Error('Unauthorized');
        }

        return response;
    };
}
